import React, { useRef, useState } from 'react';
import { Image as ImageIcon, Upload } from 'lucide-react';
import organizationsService from '../services/organizationsService';

/**
 * The organisation's logo and banner.
 *
 * The endpoints, the size limits and the magic-byte sniffing all shipped with
 * Phase 2; nothing ever called them. The visible cost was the public profile
 * page, which renders `logo_url` and `banner_url` and could therefore only
 * ever be unbranded — a company opts in to a public page and gets a blank one.
 *
 * The two differ in shape, so they are previewed in their own aspect: a logo
 * is a square tile shown at avatar size, and a banner is a wide strip. A single
 * shared preview box would show both accurately and neither usefully.
 */
const KINDS = {
  logo: {
    label: 'Logo',
    field: 'logo_url',
    hint: 'Square works best. PNG, JPEG, WebP or GIF, up to 5 MB.',
    upload: (id, file) => organizationsService.uploadLogo(id, file),
  },
  banner: {
    label: 'Banner',
    field: 'banner_url',
    hint: 'Wide image for the top of your public profile. Up to 10 MB.',
    upload: (id, file) => organizationsService.uploadBanner(id, file),
  },
};

function ImageField({ org, kind, canEdit, onSaved }) {
  const spec = KINDS[kind];
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pick = async (event) => {
    const file = event.target.files?.[0];
    // Reset immediately so choosing the SAME file again still fires a change
    // event — otherwise a failed upload cannot be retried without picking
    // something else first.
    event.target.value = '';
    if (!file) return;

    setBusy(true);
    setError('');
    try {
      await spec.upload(org.id, file);
      await onSaved();
    } catch (e) {
      const status = e?.response?.status;
      // The server distinguishes these, so the UI does too: "too big" and
      // "not an image" are both things the person can fix, and a single
      // "upload failed" would leave them guessing which.
      if (status === 413) setError('That file is too large.');
      else if (status === 422) setError('That file is not an image we support.');
      else setError('Could not upload that image.');
    } finally {
      setBusy(false);
    }
  };

  const url = org[spec.field];

  return (
    <div className="org-image-field">
      <span className="org-field-label">{spec.label}</span>

      <div className={`org-image-preview org-image-preview--${kind}`}>
        {url ? (
          <img src={url} alt={`${org.name} ${spec.label.toLowerCase()}`} />
        ) : (
          <span className="org-image-empty" aria-hidden>
            <ImageIcon size={kind === 'logo' ? 20 : 24} />
          </span>
        )}
      </div>

      {canEdit && (
        <>
          <button
            type="button"
            className="org-btn"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            <Upload size={13} aria-hidden /> {busy ? 'Uploading…' : url ? 'Replace' : 'Upload'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="org-file-input"
            onChange={pick}
          />
        </>
      )}

      <p className="org-hint">{spec.hint}</p>
      {error && <p className="org-error">{error}</p>}
    </div>
  );
}

export default function OrganizationImages({ org, canEdit, onSaved }) {
  return (
    <div className="org-images">
      <ImageField org={org} kind="logo" canEdit={canEdit} onSaved={onSaved} />
      <ImageField org={org} kind="banner" canEdit={canEdit} onSaved={onSaved} />
    </div>
  );
}
